import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import { formatDate } from '@sierralabs/core';
import { ConfigFieldControl, ConfigTextField, PageTemplate, PageTemplateAlert, PageTemplateDashboard, PageTemplateHeader, PageTemplateHeaderActions, PageTemplateNav } from '@sierralabs/core-ui';
import { EntityPicker } from '@sierralabs/entity-ui';
import { useTaskEntityData } from '@sierralabs/workflow-ui';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SlotRenderer } from '../renderers';
import { SiteComponent } from '../SiteComponentPlugin';
const regExps = { taskKey: /\{task\.key\}/g, taskName: /\{task\.name\}/g };
export const SiteDashboardPage = props => {
    const component = useSubscribeComponentChanges(SiteDashboardPagePlugin, props);
    const { editing, isHover, hoverProps } = useSiteContext();
    const classes = ['site-dashboard'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    // get task version based on the id param and configured target entity
    // use isLatest to show alert when viewing a previous version
    const { heading, entityId, entityTable, routeParam } = component.props || {};
    const params = useParams();
    const dataId = routeParam ? params[routeParam] : undefined;
    const { task, isLatest, version } = useTaskEntityData(entityId || entityTable || '', dataId);
    const url = useMemo(() => {
        // remove version query
        const url = new URL(document.location.href);
        url.searchParams.delete('version');
        return url.toString().replace(document.location.origin, '');
    }, []);
    const title = useMemo(() => {
        if (editing || !heading) {
            return heading;
        }
        // hide template strings if task is not loaded yet
        return heading
            .replace(regExps.taskKey, (task === null || task === void 0 ? void 0 : task.key) || '')
            .replace(regExps.taskName, (task === null || task === void 0 ? void 0 : task.name) || '');
    }, [editing, heading, task]);
    // console.log('route', path || component?.props?.path, component);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteDashboardPagePlugin }, props),
        React.createElement(PageTemplate, Object.assign({ className: classes.join(' ') }, hoverProps(component)),
            React.createElement(PageTemplateHeader, null,
                React.createElement(Typography, { variant: 'h2' }, title),
                React.createElement(PageTemplateHeaderActions, { flex: true },
                    React.createElement(SlotRenderer, { component: component, slot: 'header-actions' }))),
            !isLatest && (React.createElement(PageTemplateAlert, { severity: 'warning' },
                "Viewing previous version ",
                version,
                task && ` from ${formatDate(task.modified)}`,
                ".",
                ' ',
                React.createElement(Link, { to: url }, "Click here to view latest version"))),
            React.createElement(PageTemplateNav, null),
            React.createElement(PageTemplateDashboard, null,
                React.createElement(SlotRenderer, { component: component, slot: 'grid' })))));
};
export const SiteDashboardPageConfigurator = props => {
    var _a, _b, _c, _d, _e;
    const { component, remove, update } = useConfigComponent(props.component);
    const entityIdOrTable = ((_a = component.props) === null || _a === void 0 ? void 0 : _a.entityId) || ((_b = component.props) === null || _b === void 0 ? void 0 : _b.entityTable);
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(ConfigTextField, { fullWidth: true, label: 'Heading', value: ((_c = component.props) === null || _c === void 0 ? void 0 : _c.heading) || '', onChange: event => {
                    update('props.heading', event.target.value);
                }, helperText: entityIdOrTable ? 'Format: {task.key} - {task.name}' : undefined })),
        React.createElement(SiteRouteAndParamPicker, { nearestOnly: true, component: component, routeId: (_d = component.props) === null || _d === void 0 ? void 0 : _d.routeId, routeParam: (_e = component.props) === null || _e === void 0 ? void 0 : _e.routeParam, siteRoutePickerProps: {
                label: 'Entity Detail Route',
                placeholder: 'Path for Entity detail page'
            }, onChange: value => {
                updateRouteAndParam(value, 'props.routeId', 'props.routeParam').forEach(([path, value]) => update(path, value));
            } }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(EntityPicker, { fullWidth: true, variant: 'standard', label: 'Task Entity', placeholder: 'Select the Task Entity', InputProps: { disableUnderline: true }, value: entityIdOrTable, onChange: entity => {
                    if (!entity) {
                        remove('props.entityId');
                        remove('props.entityTable');
                        return;
                    }
                    const prop = entity.id ? 'entityId' : 'entityTable';
                    update(`props.${prop}`, entity.id || entity.tableName);
                } }))));
};
export const SiteDashboardPagePlugin = {
    name: 'Site Dashboard Page',
    type: 'site-dashboard-page',
    component: SiteDashboardPage,
    configurator: SiteDashboardPageConfigurator,
    slots: [
        {
            name: 'Header Actions',
            type: 'component-stack',
            slot: 'header-actions'
        },
        {
            name: 'Dashboard Grid',
            type: 'grid',
            slot: 'grid'
        }
    ]
};
//# sourceMappingURL=SiteDashboardPagePlugin.js.map